import React, { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import { Trash2 } from "lucide-react";
import { getRequest, postRequest, updateRequest } from "../../../services";
import { Select, Spin } from "antd";

const { Option } = Select;

const SurveyModal = ({
  isOpen,
  onClose = () => {},
  onSave,
  editingData,
}) => {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
  } = useForm();

  // Question state
  const [questions, setQuestions] = useState([
    {
      question: "",
      options: ["", "", "", ""],
    },
  ]);

  // Campus/Class state
  const [campusData, setCampusData] = useState([]);
  const [selectedCampus, setSelectedCampus] = useState("");
  const [selectedClasses, setSelectedClasses] = useState([]); // MULTI SELECT
  const [classList, setClassList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [surveyType, setSurveyType] = useState("");

  // Refs to track if we need to set class after data loads
  const pendingClassIds = useRef([]);

  // Fetch campus/class data
  useEffect(() => {
    if (isOpen) {
      setLoading(true);
      getRequest("/api/Campus/GetCampusDetail")
        .then((res) => {
          setCampusData(res.data);
          setLoading(false);
        })
        .catch((err) => {
          setError("Failed to fetch campus data");
          setLoading(false);
        });
    }
  }, [isOpen]);

  // When editingData changes, set up form and pending class
  useEffect(() => {
    if (editingData) {
      // Set form fields for editing
      reset({
        surveyName: editingData.surveyName || "",
        objective: editingData.objective || "",
        audience: editingData.audience || "",
      });
      // Set questions for editing
      if (editingData.questions && Array.isArray(editingData.questions)) {
        setQuestions(
          editingData.questions.map((q) => ({
            id: q.id,
            question: q.questionText || "",
            options: Array.isArray(q.options)
              ? [...q.options, "", "", "", ""].slice(0, 4)
              : ["", "", "", ""],
          }))
        );
      } else {
        setQuestions([
          {
            question: "",
            options: ["", "", "", ""],
          },
        ]);
      }
      // Set campus, and store class to set after lists are ready
      setSelectedCampus(editingData.campusId || editingData.campus || "");
      // classId can be array or string
      let classIds = [];
      if (Array.isArray(editingData.classId)) {
        classIds = editingData.classId;
      } else if (editingData.classId) {
        classIds = [editingData.classId];
      } else if (editingData.cls) {
        classIds = Array.isArray(editingData.cls) ? editingData.cls : [editingData.cls];
      }
      pendingClassIds.current = classIds;
      setSurveyType(editingData.surveyType || "");
    } else {
      setQuestions([
        {
          question: "",
          options: ["", "", "", ""],
        },
      ]);
      setSelectedCampus("");
      setSelectedClasses([]);
      setSurveyType("");
      reset({
        surveyName: "",
        objective: "",
        audience: "",
      });
      pendingClassIds.current = [];
    }
  }, [editingData, reset]);

  // Handle campus/class selection
  useEffect(() => {
    if (selectedCampus) {
      const campus = campusData.find((c) => String(c.id) === String(selectedCampus));
      setClassList(campus?.classes || []);
      // If editing, set class from pendingClassIds, else reset
      if (pendingClassIds.current && pendingClassIds.current.length > 0) {
        setSelectedClasses(
          pendingClassIds.current.map(String).filter((id) =>
            (campus?.classes || []).some((cls) => String(cls.id) === String(id))
          )
        );
        pendingClassIds.current = [];
      } else {
        setSelectedClasses([]);
      }
    } else {
      setClassList([]);
      setSelectedClasses([]);
    }
  }, [selectedCampus, campusData]);

  const onSubmit = async (data) => {
    // Map questions to API format
    const formattedQuestions = questions.map((q) => {
      // If editing, preserve question id if present
      const base = {
        questionText: q.question,
        options: q.options.filter((opt) => opt && opt.trim() !== ""),
      };
      if (q.id) base.id = q.id;
      return base;
    });

    const payload = {
      surveyName: data.surveyName,
      objective: data.objective,
      surveyType: surveyType,
      audience: data.audience,
      campusId: selectedCampus,
      classId: selectedClasses, // MULTI SELECTED CLASS IDS ARRAY
      questions: formattedQuestions,
    };

    try {
      if (editingData && editingData.id) {
        // Update (PUT)
        await updateRequest(`/api/Survey/${editingData.id}`, {
          id: editingData.id,
          ...payload,
        });
        if (onSave) onSave({ ...payload, id: editingData.id });
      } else {
        // Create (POST)
        await postRequest("/api/Survey", payload);
        if (onSave) onSave(payload);
      }
      reset();
      setQuestions([
        {
          question: "",
          options: ["", "", "", ""],
        },
      ]);
      setSelectedCampus("");
      setSelectedClasses([]);
      setSurveyType("");
      onClose();
    } catch (err) {
      alert("Failed to submit survey");
    }
  };

  const handleClose = () => {
    reset();
    setQuestions([
      {
        question: "",
        options: ["", "", "", ""],
      },
    ]);
    setSelectedCampus("");
    setSelectedClasses([]);
    setSurveyType("");
    if (onClose) {
      onClose();
    }
    pendingClassIds.current = [];
  };

  // Question handlers
  const handleAddQuestion = () => {
    setQuestions([
      ...questions,
      {
        question: "",
        options: ["", "", "", ""],
      },
    ]);
  };

  const handleDeleteQuestion = (indexToDelete) => {
    setQuestions(questions.filter((_, idx) => idx !== indexToDelete));
  };

  const handleQuestionChange = (index, value) => {
    const newQuestions = [...questions];
    newQuestions[index].question = value;
    setQuestions(newQuestions);
  };

  const handleOptionChange = (qIdx, oIdx, value) => {
    const newQuestions = [...questions];
    newQuestions[qIdx].options[oIdx] = value;
    setQuestions(newQuestions);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-gray-900 bg-opacity-50 flex justify-center items-center z-50">
      <div className="bg-white w-2/4 max-h-[80vh] overflow-y-auto rounded-lg shadow-lg p-6">
        <h2 className="text-2xl font-semibold mb-4">
          {editingData ? "Edit Survey" : "Add Surveys"}
        </h2>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          <div className="grid grid-cols-2 gap-6">
            <div>
              <label className="block mb-1 font-medium">
                Survey Name
              </label>
              <input
                {...register("surveyName", {
                  required: "This field is required",
                })}
                className="w-full border border-gray-300 p-2 rounded"
                placeholder="Enter survey name"
              />
            </div>
            <div className="">
              <label className="block mb-1 font-medium">Objective</label>
              <input
                {...register("objective")}
                className="w-full border border-gray-300 p-2 rounded"
                placeholder="Enter objectives"
              />
            </div>
            <div>
              <label className="block mb-1 font-medium">Select audience</label>
              <select
                className="w-full border border-gray-300 rounded p-2"
                {...register("audience")}
              >
                <option>Parent</option>
                <option>Teacher</option>
                <option>Learner</option>
              </select>
            </div>
            <div>
              <label className="block mb-1 font-medium">Select Campus</label>
              <select
                className="w-full border border-gray-300 rounded p-2"
                value={selectedCampus}
                onChange={e => setSelectedCampus(e.target.value)}
                required
              >
                <option value="">Select Campus</option>
                {campusData.map((campus) => (
                  <option key={campus.id} value={campus.id}>{campus.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block mb-1 font-medium">Select Class</label>
              <Select
                mode="multiple"
                allowClear
                showArrow
                placeholder="Select Class"
                value={selectedClasses}
                onChange={setSelectedClasses}
                className="w-full"
                disabled={!selectedCampus}
                optionLabelProp="label"
                dropdownStyle={{ zIndex: 9999 }}
                getPopupContainer={trigger => trigger.parentNode}
              >
                {classList.map((cls) => (
                  <Option key={cls.id} value={cls.id} label={cls.className}>
                    {cls.className}
                  </Option>
                ))}
              </Select>
            </div>
            <div>
              <label className="block mb-1 font-medium">Survey Type</label>
              <input
                type="text"
                className="w-full border border-gray-300 p-2 rounded"
                value={surveyType}
                onChange={e => setSurveyType(e.target.value)}
                placeholder="Enter survey type"
                required
              />
            </div>
          </div>

          {/* Question Layout */}
          <div className="mt-8">
            <div className="flex justify-between items-center mb-4">
              <label className="block text-lg font-medium">Questions</label>
              <button
                type="button"
                onClick={handleAddQuestion}
                className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
              >
                Add Question
              </button>
            </div>
            <div className="space-y-6">
              {questions.map((questionObj, qIndex) => (
                <div key={qIndex} className="relative border border-gray-200 p-2 rounded">
                  <div className="flex justify-between items-start mb-2">
                    <label className="block font-medium">
                      Question {qIndex + 1}
                    </label>
                    {questions.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleDeleteQuestion(qIndex)}
                        className="text-red-500 hover:text-red-700"
                        title="Delete Question"
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    )}
                  </div>
                  <div>
                    <textarea
                      value={questionObj.question}
                      onChange={(e) => handleQuestionChange(qIndex, e.target.value)}
                      className="w-full border border-gray-300 p-2 rounded"
                      placeholder="Enter your question"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-3">
                    {questionObj.options.map((option, oIndex) => (
                      <div key={oIndex}>
                        <input
                          value={option}
                          onChange={(e) => handleOptionChange(qIndex, oIndex, e.target.value)}
                          className="w-full border border-gray-300 p-2 rounded"
                          placeholder={`Enter option ${oIndex + 1}`}
                        />
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
  
          <div className="flex justify-end space-x-4 mt-6">
            <button
              type="button"
              onClick={handleClose}
              className="bg-gray-400 text-white px-6 py-2 rounded"
            >
              Close
            </button>
            <button
              type="submit"
              className="bg-[#241763] text-white px-6 py-2 rounded"
            >
              {editingData ? "Update" : "Submit"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SurveyModal;
